import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';

const TABS = ['Overview', 'Customers', 'Spare', 'Reports'];
const SPARE_OPERATION_TABS = [
  { id: 'cash-out', label: 'Cash Out' },
  { id: 'cash-in', label: 'Cash In' },
];
const PICKUP_LOCATIONS = ['Walmart Supercenter- 409 N Marketplace Blvd, Lansing, MI 48917', 'Walmart - 3225 Centre Blvd, Lansing, MI 48912'];
const DEMO_SMS_ACCESS_TOKEN = 'SpareDemo2026';
const DEMO_API_BASE_URL = (process.env.REACT_APP_DEMO_API_BASE_URL || 'https://vatmapistaging.gotspare.com').replace(/\/$/, '');
const DEMO_WEB_BASE_URL = 'https://web.gotspare.com';
const DEMO_CUSTOMER_ENDPOINT = `${DEMO_API_BASE_URL}/demo/customer`;
const DEMO_BARCODE_BASE_URL = `${DEMO_WEB_BASE_URL}/demo/barcode`;
const STATUS_OPTIONS = ['All', 'Pending', 'Sent', 'Confirmed', 'Redeemed', 'Expired'];

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(value) {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '$0.00';
  }
  return numeric.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function getRemainingLabel(expiresAt) {
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) {
    return 'Expired';
  }

  const totalMinutes = Math.floor(diffMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m remaining`;
}

function parsePickupLocation(selection) {
  const value = (selection || '').trim();
  if (!value) {
    return { locationName: '', locationAddress: '' };
  }

  const separatorIndex = value.indexOf('-');
  if (separatorIndex === -1) {
    return { locationName: value, locationAddress: '' };
  }

  return {
    locationName: value.slice(0, separatorIndex).trim(),
    locationAddress: value.slice(separatorIndex + 1).trim(),
  };
}

function normalizePhone(value) {
  const raw = (value || '').trim();
  if (!raw) {
    return '';
  }

  const digitsOnly = raw.replace(/[^\d]/g, '');
  if (!digitsOnly) {
    return '';
  }

  return `+${digitsOnly}`;
}

function buildDemoBarcodeUrl(amountInCents) {
  return `${DEMO_BARCODE_BASE_URL}/cash-out/${amountInCents}`;
}

function buildDemoCashInUrl(amountInCents) {
  return `${DEMO_BARCODE_BASE_URL}/cash-in/${amountInCents}`;
}

function buildDemoCashInBarcodeUrl(amountInCents, locationSelection = '') {
  const parsedLocation = parsePickupLocation(locationSelection);
  const queryParams = new URLSearchParams();

  if (parsedLocation.locationName) {
    queryParams.set('merchant', parsedLocation.locationName);
  }

  if (parsedLocation.locationAddress) {
    queryParams.set('address', parsedLocation.locationAddress);
  }

  const queryString = queryParams.toString();
  const baseUrl = `${DEMO_BARCODE_BASE_URL}/cash-in/${amountInCents}`;
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

async function postJson(url, payload) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.message || 'Request failed.');
  }

  return data;
}

function buildSearchableText(request) {
  return [
    request.userName,
    request.phone,
    request.zipcode,
    request.location,
    request.address,
    request.stageLabel,
  ].filter(Boolean).join(' ').toLowerCase();
}

function TheBankDemoFlow() {
  const location = useLocation();
  const isCashInPublicPage = location.pathname === '/demo/the-bank/cash-in';
  const spareLogo = `${process.env.PUBLIC_URL || ''}/images/spare_logo_full.png`;
  const [, setNowTick] = useState(Date.now());
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('Spare');
  const [spareView, setSpareView] = useState('dashboard');
  const [spareOperation, setSpareOperation] = useState('cash-out');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [cashOutForm, setCashOutForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    zipcode: '',
    amount: '',
    location: '',
  });
  const [cashInForm, setCashInForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    amount: '',
  });
  const [cashOutRequests, setCashOutRequests] = useState([]);
  const [cashInRequests, setCashInRequests] = useState([]);

  const publicParams = new URLSearchParams(location.search);
  const publicCashInToken = publicParams.get('token') || '';
  const publicCashInAmount = Number(publicParams.get('amount') || '0');
  const hasValidPublicCashInAmount = Number.isFinite(publicCashInAmount) && publicCashInAmount > 0;
  const [publicCashInForm, setPublicCashInForm] = useState({
    address: '',
    location: '',
  });
  const [isSubmittingPublicCashIn, setIsSubmittingPublicCashIn] = useState(false);
  const [publicCashInError, setPublicCashInError] = useState('');

  useEffect(() => {
    if (isCashInPublicPage || !isLoggedIn) {
      return undefined;
    }

    const interval = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(interval);
  }, [isCashInPublicPage, isLoggedIn]);

  const getRequestStatus = (item) => {
    if (item.status === 'Redeemed') {
      return 'Redeemed';
    }
    if (item.status === 'Confirmed') {
      return 'Confirmed';
    }
    if (new Date(item.expiresAt).getTime() <= Date.now()) {
      return 'Expired';
    }
    return item.status || 'Sent';
  };

  const decorateRequest = (item) => {
    const status = getRequestStatus(item);
    return {
      ...item,
      status,
      remainingLabel: status === 'Expired' || status === 'Redeemed' || status === 'Confirmed'
        ? status
        : getRemainingLabel(item.expiresAt),
    };
  };

  const currentRequests = (spareOperation === 'cash-out' ? cashOutRequests : cashInRequests).map(decorateRequest);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredRequests = currentRequests.filter((item) => {
    const statusMatches = statusFilter === 'All' || item.status === statusFilter;
    const searchMatches = !normalizedQuery || buildSearchableText(item).includes(normalizedQuery);
    return statusMatches && searchMatches;
  });

  const now = Date.now();
  const activeCount = currentRequests.filter((item) => item.status === 'Pending' || item.status === 'Sent').length;
  const expiringSoonCount = currentRequests.filter((item) => (
    (item.status === 'Pending' || item.status === 'Sent')
    && (new Date(item.expiresAt).getTime() - now) <= 2 * 60 * 60 * 1000
  )).length;
  const todaySentCount = currentRequests.filter((item) => {
    if (!item.createdAt || (item.status !== 'Sent' && item.status !== 'Confirmed')) {
      return false;
    }
    const created = new Date(item.createdAt);
    const today = new Date();
    return created.getDate() === today.getDate()
      && created.getMonth() === today.getMonth()
      && created.getFullYear() === today.getFullYear();
  }).length;

  const updateCredentials = (event) => {
    const { name, value } = event.target;
    setCredentials((previous) => ({ ...previous, [name]: value }));
  };

  const handleLogin = (event) => {
    event.preventDefault();
    if (!credentials.username || !credentials.password) {
      return;
    }
    setIsLoggedIn(true);
    setActiveTab('Spare');
    setSpareView('dashboard');
    if (cashOutRequests.length === 0) {
      const nowIso = new Date().toISOString();
      setCashOutRequests([
        {
          id: 'seed-out-1',
          userName: 'Jordan Lee',
          phone: '+12135559821',
          zipcode: '91601',
          amount: 120,
          location: PICKUP_LOCATIONS[0],
          createdAt: nowIso,
          expiresAt: new Date(Date.now() + 90 * 60 * 1000).toISOString(),
          status: 'Sent',
        },
        {
          id: 'seed-out-2',
          userName: 'Mia Garcia',
          phone: '+13234567891',
          zipcode: '90210',
          amount: 85,
          location: PICKUP_LOCATIONS[1],
          createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          status: 'Pending',
        },
        {
          id: 'seed-out-3',
          userName: 'Noah Patel',
          phone: '+14085556789',
          zipcode: '10001',
          amount: 65,
          location: PICKUP_LOCATIONS[0],
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
          status: 'Sent',
        },
        {
          id: 'seed-out-4',
          userName: 'Aria Johnson',
          phone: '+17145550999',
          zipcode: '92612',
          amount: 200,
          location: PICKUP_LOCATIONS[1],
          createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
          status: 'Redeemed',
        },
      ]);
    }
    if (cashInRequests.length === 0) {
      setCashInRequests([
        {
          id: 'seed-in-1',
          userName: 'Avery Smith',
          phone: '+12135550101',
          amount: 300,
          address: '102 Main St, Los Angeles, CA 91601',
          location: PICKUP_LOCATIONS[0],
          stageLabel: 'Drop-off confirmed',
          createdAt: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
          status: 'Confirmed',
        },
        {
          id: 'seed-in-2',
          userName: 'Taylor Brooks',
          phone: '+13235550102',
          amount: 150,
          stageLabel: 'Waiting for location selection',
          createdAt: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 23 * 60 * 60 * 1000).toISOString(),
          status: 'Sent',
        },
      ]);
    }
  };

  const updateCashOutForm = (event) => {
    const { name, value } = event.target;
    setCashOutForm((previous) => ({ ...previous, [name]: value }));
  };

  const updateCashInForm = (event) => {
    const { name, value } = event.target;
    setCashInForm((previous) => ({ ...previous, [name]: value }));
  };

  const startRequest = () => {
    setRequestError('');
    setRequestSuccess('');
    setSpareView('form');
  };

  const submitCashOutRequest = async (event) => {
    event.preventDefault();
    setRequestError('');
    setRequestSuccess('');

    const parsedAmount = Number(cashOutForm.amount);
    const amountCents = Math.round(parsedAmount * 100);
    const parsedLocation = parsePickupLocation(cashOutForm.location);
    const normalizedPhone = normalizePhone(cashOutForm.phone);
    const isValidPhone = /^\+\d{10,15}$/.test(normalizedPhone);
    if (
      !cashOutForm.firstName.trim()
      || !cashOutForm.lastName.trim()
      || !isValidPhone
      || !cashOutForm.zipcode.trim()
      || !cashOutForm.location
      || !parsedLocation.locationName
      || !parsedLocation.locationAddress
      || Number.isNaN(parsedAmount)
      || parsedAmount <= 0
    ) {
      setRequestError('Please complete all required fields with valid values.');
      return;
    }

    const payload = {
      access_token: DEMO_SMS_ACCESS_TOKEN,
      method: 'cash-out',
      first_name: cashOutForm.firstName.trim(),
      last_name: cashOutForm.lastName.trim(),
      phone: normalizedPhone,
      location_name: parsedLocation.locationName,
      location_address: parsedLocation.locationAddress,
      amount: amountCents,
      url: buildDemoBarcodeUrl(amountCents),
    };

    setIsSendingSms(true);

    try {
      await postJson(DEMO_CUSTOMER_ENDPOINT, payload);

      const newRequest = {
        id: `${Date.now()}`,
        userName: `${payload.first_name} ${payload.last_name}`,
        phone: payload.phone,
        zipcode: cashOutForm.zipcode.trim(),
        amount: parsedAmount,
        location: cashOutForm.location,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        status: 'Sent',
      };

      setCashOutRequests((previous) => [newRequest, ...previous]);
      setRequestSuccess('Withdrawal SMS sent successfully.');
      setCashOutForm({
        firstName: '',
        lastName: '',
        phone: '',
        zipcode: '',
        amount: '',
        location: '',
      });
      setSpareView('dashboard');
    } catch (error) {
      setRequestError(error.message || 'Failed to send SMS request.');
    } finally {
      setIsSendingSms(false);
    }
  };

  const submitCashInRequest = async (event) => {
    event.preventDefault();
    setRequestError('');
    setRequestSuccess('');

    const parsedAmount = Number(cashInForm.amount);
    const amountCents = Math.round(parsedAmount * 100);
    const normalizedPhone = normalizePhone(cashInForm.phone);
    const isValidPhone = /^\+\d{10,15}$/.test(normalizedPhone);
    const firstName = cashInForm.firstName.trim();
    const lastName = cashInForm.lastName.trim();

    if (
      !firstName
      || !lastName
      || !isValidPhone
      || Number.isNaN(parsedAmount)
      || parsedAmount <= 0
    ) {
      setRequestError('Please complete all required fields with valid values.');
      return;
    }

    const payload = {
      access_token: DEMO_SMS_ACCESS_TOKEN,
      method: 'cash-in',
      first_name: firstName,
      last_name: lastName,
      name: `${firstName} ${lastName}`,
      phone: normalizedPhone,
      amount: amountCents,
      url: buildDemoCashInUrl(amountCents),
    };

    setIsSendingSms(true);

    try {
      await postJson(DEMO_CUSTOMER_ENDPOINT, payload);

      const newRequest = {
        id: `${Date.now()}`,
        userName: payload.name,
        phone: payload.phone,
        amount: parsedAmount,
        stageLabel: 'Waiting for location selection',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        status: 'Sent',
      };

      setCashInRequests((previous) => [newRequest, ...previous]);
      setRequestSuccess('Deposit SMS sent successfully.');
      setCashInForm({
        firstName: '',
        lastName: '',
        phone: '',
        amount: '',
      });
      setSpareView('dashboard');
    } catch (error) {
      setRequestError(error.message || 'Failed to send cash in text.');
    } finally {
      setIsSendingSms(false);
    }
  };

  const submitPublicCashInLocation = async (event) => {
    event.preventDefault();
    setPublicCashInError('');

    const parsedLocation = parsePickupLocation(publicCashInForm.location);
    if (
      !publicCashInToken
      || !publicCashInForm.address.trim()
      || !publicCashInForm.location
      || !parsedLocation.locationName
      || !parsedLocation.locationAddress
    ) {
      setPublicCashInError('Please provide your address and choose a drop-off location.');
      return;
    }

    setIsSubmittingPublicCashIn(true);

    try {
      const barcodeUrl = buildDemoCashInBarcodeUrl(publicCashInAmount, publicCashInForm.location);
      window.location.assign(barcodeUrl);
    } catch (error) {
      setPublicCashInError(error.message || 'Failed to confirm your cash drop off.');
    } finally {
      setIsSubmittingPublicCashIn(false);
    }
  };

  const renderLogin = () => (
    <div className="the-bank-demo__login-screen">
      <div className="the-bank-demo__login-card">
        <div className="the-bank-demo__login-brand">
          <img src={spareLogo} alt="Spare" className="the-bank-demo__logo" />
          <p className="the-bank-demo__eyebrow">Spare Customer Service Portal</p>
          <h1>The Bank</h1>
          <p>Spare-powered demo sign in for customer service agents.</p>
        </div>
        <form onSubmit={handleLogin}>
          <label htmlFor="username">Username</label>
          <input
            id="username"
            name="username"
            type="text"
            value={credentials.username}
            onChange={updateCredentials}
            placeholder="agent.thebank"
            required
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            value={credentials.password}
            onChange={updateCredentials}
            placeholder="Password"
            required
          />

          <button type="submit">Log In</button>
        </form>
      </div>
    </div>
  );

  const renderSpareDashboard = () => (
    <div className="the-bank-demo__panel">
      <div className="the-bank-demo__panel-header">
        <div>
          <h2>{spareOperation === 'cash-out' ? 'Spare Cash Out Dashboard' : 'Spare Cash In Dashboard'}</h2>
          <p>
            {spareOperation === 'cash-out'
              ? 'Create and manage Spare cash pickup requests sent by SMS.'
              : 'Send cash deposit texts and track location selection requests before barcode completion.'}
          </p>
        </div>
        <button className="the-bank-demo__plus-btn" type="button" onClick={startRequest} aria-label="Create new request">
          +
        </button>
      </div>

      <div className="the-bank-demo__subtabs" role="tablist" aria-label="Spare operation type">
        {SPARE_OPERATION_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={spareOperation === tab.id ? 'is-active' : ''}
            onClick={() => {
              setSpareOperation(tab.id);
              setSpareView('dashboard');
              setQuery('');
              setStatusFilter('All');
              setRequestError('');
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {requestSuccess ? <p className="the-bank-demo__status-ok">{requestSuccess}</p> : null}

      <section className="the-bank-demo__kpis">
        <article className="the-bank-demo__kpi">
          <p>Total requests</p>
          <h3>{currentRequests.length}</h3>
        </article>
        <article className="the-bank-demo__kpi">
          <p>Active now</p>
          <h3>{activeCount}</h3>
        </article>
        <article className="the-bank-demo__kpi">
          <p>Expiring in 2h</p>
          <h3>{expiringSoonCount}</h3>
        </article>
        <article className="the-bank-demo__kpi">
          <p>Sent today</p>
          <h3>{todaySentCount}</h3>
        </article>
      </section>

      <section className="the-bank-demo__filters" aria-label="Request filters">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={spareOperation === 'cash-out' ? 'Search by name, phone, zip, or location' : 'Search by name, phone, address, or stage'}
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </section>

      {filteredRequests.length === 0 ? (
        <div className="the-bank-demo__empty-state">
          <h3>{currentRequests.length === 0 ? 'No requests yet' : 'No requests match the current filters'}</h3>
          <p>
            {currentRequests.length === 0
              ? `Click + to create a new Spare ${spareOperation === 'cash-out' ? 'cash out' : 'cash in'} request.`
              : 'Try clearing search or changing status filter.'}
          </p>
        </div>
      ) : (
        <div className="the-bank-demo__tiles">
          {filteredRequests.map((request) => (
            <article key={request.id} className="the-bank-demo__tile">
              <div className="the-bank-demo__tile-head">
                <h3>{request.userName}</h3>
                <span className={`the-bank-demo__status-pill status-${request.status.toLowerCase()}`}>{request.status}</span>
              </div>
              <p><strong>Status:</strong> {request.remainingLabel}</p>
              <p><strong>Phone:</strong> {request.phone}</p>
              {spareOperation === 'cash-out' ? <p><strong>Zipcode:</strong> {request.zipcode || 'N/A'}</p> : null}
              {request.address ? <p><strong>Address:</strong> {request.address}</p> : null}
              <p><strong>{spareOperation === 'cash-out' ? 'Location' : 'Drop-off location'}:</strong> {request.location || 'Waiting for customer selection'}</p>
              {spareOperation === 'cash-in' ? <p><strong>Stage:</strong> {request.stageLabel || 'Waiting for location selection'}</p> : null}
              <p><strong>Amount:</strong> {formatCurrency(request.amount)}</p>
              <p><strong>Created:</strong> {formatDateTime(request.createdAt)}</p>
              <p><strong>Expires:</strong> {formatDateTime(request.expiresAt)}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );

  const renderCashOutForm = () => (
    <form className="the-bank-demo__form" onSubmit={submitCashOutRequest}>
      <label htmlFor="firstName">First name</label>
      <input
        id="firstName"
        name="firstName"
        type="text"
        value={cashOutForm.firstName}
        onChange={updateCashOutForm}
        required
      />

      <label htmlFor="lastName">Last name</label>
      <input
        id="lastName"
        name="lastName"
        type="text"
        value={cashOutForm.lastName}
        onChange={updateCashOutForm}
        required
      />

      <label htmlFor="phone">Phone</label>
      <PhoneInput
        country="us"
        value={cashOutForm.phone}
        onChange={(value) => setCashOutForm((previous) => ({ ...previous, phone: value }))}
        inputProps={{
          id: 'phone',
          name: 'phone',
          required: true,
        }}
        inputStyle={{ width: '100%' }}
        placeholder="+1XXXXXXXXXX"
      />

      <label htmlFor="zipcode">Zipcode</label>
      <input
        id="zipcode"
        name="zipcode"
        type="text"
        value={cashOutForm.zipcode}
        onChange={updateCashOutForm}
        required
      />

      <label htmlFor="amount">Amount</label>
      <input
        id="amount"
        name="amount"
        type="number"
        min="0.01"
        step="0.01"
        value={cashOutForm.amount}
        onChange={updateCashOutForm}
        required
      />

      <label htmlFor="location">Pickup location</label>
      <select
        id="location"
        name="location"
        value={cashOutForm.location}
        onChange={updateCashOutForm}
        disabled={!cashOutForm.zipcode.trim()}
        required
      >
        {!cashOutForm.zipcode.trim() ? (
          <option value="">Enter in a zipcode</option>
        ) : null}
        {cashOutForm.zipcode.trim() ? (
          <option value="">Select a location</option>
        ) : null}
        {cashOutForm.zipcode.trim()
          ? PICKUP_LOCATIONS.map((pickupLocation) => (
            <option key={pickupLocation} value={pickupLocation}>{pickupLocation}</option>
          ))
          : null}
      </select>

      <div className="the-bank-demo__form-actions">
        <button type="button" className="the-bank-demo__ghost-btn" onClick={() => setSpareView('dashboard')}>
          Cancel
        </button>
        <button type="submit" disabled={isSendingSms}>
          {isSendingSms ? 'Sending...' : 'Send SMS'}
        </button>
      </div>
    </form>
  );

  const renderCashInForm = () => (
    <form className="the-bank-demo__form" onSubmit={submitCashInRequest}>
      <label htmlFor="cashInFirstName">First name</label>
      <input
        id="cashInFirstName"
        name="firstName"
        type="text"
        value={cashInForm.firstName}
        onChange={updateCashInForm}
        required
      />

      <label htmlFor="cashInLastName">Last name</label>
      <input
        id="cashInLastName"
        name="lastName"
        type="text"
        value={cashInForm.lastName}
        onChange={updateCashInForm}
        required
      />

      <label htmlFor="cashInPhone">Phone</label>
      <PhoneInput
        country="us"
        value={cashInForm.phone}
        onChange={(value) => setCashInForm((previous) => ({ ...previous, phone: value }))}
        inputProps={{
          id: 'cashInPhone',
          name: 'phone',
          required: true,
        }}
        inputStyle={{ width: '100%' }}
        placeholder="+1XXXXXXXXXX"
      />

      <label htmlFor="cashInAmount">Amount</label>
      <input
        id="cashInAmount"
        name="amount"
        type="number"
        min="0.01"
        step="0.01"
        value={cashInForm.amount}
        onChange={updateCashInForm}
        required
      />

      <div className="the-bank-demo__form-actions">
        <button type="button" className="the-bank-demo__ghost-btn" onClick={() => setSpareView('dashboard')}>
          Cancel
        </button>
        <button type="submit" disabled={isSendingSms}>
          {isSendingSms ? 'Sending...' : 'Send Text'}
        </button>
      </div>
    </form>
  );

  const renderSingleRequestForm = () => (
    <div className="the-bank-demo__panel">
      <div className="the-bank-demo__form-header">
        <h2>{spareOperation === 'cash-out' ? 'Create Spare Cash Out Request' : 'Create Spare Cash In Request'}</h2>
        <p>
          {spareOperation === 'cash-out'
            ? 'Single-step form that sends pickup details to the SMS API.'
            : 'Send a text with a link for the customer to choose a deposit location before their barcode is shown.'}
        </p>
      </div>

      <div className="the-bank-demo__subtabs" role="tablist" aria-label="Spare operation type">
        {SPARE_OPERATION_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={spareOperation === tab.id ? 'is-active' : ''}
            onClick={() => {
              setSpareOperation(tab.id);
              setRequestError('');
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {requestError ? <p className="the-bank-demo__status-error">{requestError}</p> : null}

      {spareOperation === 'cash-out' ? renderCashOutForm() : renderCashInForm()}
    </div>
  );

  const renderTabContent = () => {
    if (activeTab !== 'Spare') {
      return (
        <div className="the-bank-demo__panel the-bank-demo__placeholder">
          <h2>{activeTab}</h2>
          <p>This demo focuses on the Spare tab workflow.</p>
        </div>
      );
    }

    if (spareView === 'form') {
      return renderSingleRequestForm();
    }

    return renderSpareDashboard();
  };

  const renderPortal = () => (
    <div className="the-bank-demo__portal">
      <aside className="the-bank-demo__sidebar">
        <div className="the-bank-demo__brand">
          <img src={spareLogo} alt="Spare" className="the-bank-demo__logo the-bank-demo__logo--sidebar" />
          <p>Spare for Customer Service</p>
          <h2>The Bank</h2>
        </div>

        <nav>
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={tab === activeTab ? 'is-active' : ''}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'Spare') {
                  setSpareView('dashboard');
                }
              }}
            >
              {tab}
            </button>
          ))}
        </nav>
      </aside>

      <main className="the-bank-demo__content">
        <header className="the-bank-demo__topbar">
          <div>
            <p className="the-bank-demo__topbar-label">Environment</p>
            <h1>Customer Service Demo</h1>
          </div>
          <div className="the-bank-demo__topbar-right">
            <span className="the-bank-demo__env-pill">Demo</span>
            <span className="the-bank-demo__agent-pill">{credentials.username || 'agent.thebank'}</span>
          </div>
        </header>
        {renderTabContent()}
      </main>
    </div>
  );

  const renderPublicCashInPage = () => {
    return (
      <div className="the-bank-demo__login-screen">
        <div className="the-bank-demo__login-card the-bank-demo__cash-in-public-card">
          <div className="the-bank-demo__login-brand">
            <img src={spareLogo} alt="Spare" className="the-bank-demo__logo" />
            <p className="the-bank-demo__eyebrow">Cash Drop Off Request</p>
            <h1>Select a deposit location</h1>
            <p>Choose where you would like to drop off your cash before we show the barcode.</p>
          </div>

          {publicCashInError ? <p className="the-bank-demo__status-error">{publicCashInError}</p> : null}

          {!publicCashInToken || !hasValidPublicCashInAmount ? (
            <p className="the-bank-demo__status-error">This cash in link is missing required details. Please request a new text.</p>
          ) : (
            <form onSubmit={submitPublicCashInLocation} className="the-bank-demo__form">
              <label htmlFor="cashInAddress">Address</label>
              <input
                id="cashInAddress"
                name="address"
                type="text"
                value={publicCashInForm.address}
                onChange={(event) => setPublicCashInForm((previous) => ({ ...previous, address: event.target.value }))}
                placeholder="Enter your address"
                required
              />

              <label htmlFor="cashInLocation">Drop-off location</label>
              <select
                id="cashInLocation"
                name="location"
                value={publicCashInForm.location}
                onChange={(event) => setPublicCashInForm((previous) => ({ ...previous, location: event.target.value }))}
                disabled={!publicCashInForm.address.trim()}
                required
              >
                {!publicCashInForm.address.trim() ? <option value="">Enter your address first</option> : null}
                {publicCashInForm.address.trim() ? <option value="">Select a Walmart location</option> : null}
                {publicCashInForm.address.trim()
                  ? PICKUP_LOCATIONS.map((pickupLocation) => (
                    <option key={pickupLocation} value={pickupLocation}>{pickupLocation}</option>
                  ))
                  : null}
              </select>

              <button type="submit" disabled={!publicCashInForm.location || isSubmittingPublicCashIn}>
                {isSubmittingPublicCashIn ? 'Opening barcode...' : 'Confirm location'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  };

  if (isCashInPublicPage) {
    return <div className="the-bank-demo">{renderPublicCashInPage()}</div>;
  }

  return <div className="the-bank-demo">{isLoggedIn ? renderPortal() : renderLogin()}</div>;
}

export default TheBankDemoFlow;
